/**
 * @description Settings for Sensai AI controller
 * @module API controller / Sensai
 * @requires constants/sensai
 * @requires config
 */

import { SENSAI_CONFIG, SENSAI_ENDPOINTS, BASE_URL } from 'constants/sensai'
import { throwError, timer, range, zip } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import { ApiError } from 'classes/errors'

/**
 * @description Validates required parameters for Sensai API requests
 * @param {Object} params - Parameters to validate
 * @param {number} [params.accountId] - Account identifier
 * @param {string} [params.userId] - User identifier
 * @throws {Error} If neither accountId nor userId is provided
 * @throws {Error} If parameters are of invalid type
 */
const validateSensaiParams = ({ accountId, userId }) => {
  if (!accountId && !userId) {
    throw new Error('Either accountId or userId is required')
  }
}

module.exports = {
  getStatus: {
    validateRequest: true,
    ignoreNotFound: true,
    /**
     * @description Get Sensai status for an account
     * @param {Object} options
     * @param {number} options.accountId - Account identifier
     * @returns {Object} Request configuration
     * @throws {Error} If accountId is invalid
     */
    settings: ({ accountId }) => {
      validateSensaiParams({ accountId })

      return {
        endpoint: SENSAI_ENDPOINTS.STATUS(accountId),
        method: 'GET',
      }
    },
  },

  getCoaches: {
    useExternalRequest: true,
    validateRequest: true,
    /**
     * @description Override retry logic - don't retry only on 404 errors for getCoaches
     * This prevents multiple API calls when coaches are not found (404)
     * Other errors will be retried according to default retry logic
     * @param {Observable} attempts - Observable of error attempts
     * @returns {Observable} Observable that throws error immediately on 404, retries others
     */
    retryWhen(attempts) {
      const MAX_TRIES = 2
      const RETRY_TIME = 500

      return zip(attempts, range(1, MAX_TRIES + 1)).pipe(
        mergeMap(([errorString, i]) => {
          const error = this.decryptResponse(errorString)
          // Only skip retry immediately if error status is 404
          if (error?._status === 404) {
            return throwError(() => new ApiError(error))
          }

          // For other errors, use default retry logic
          if (i > MAX_TRIES) {
            return throwError(() => new ApiError(error))
          }

          // Retry after delay for non-404 errors
          return timer(i * RETRY_TIME)
        }),
      )
    },
    /**
     * @description Retrieve list of AI coaches
     * @param {Object} options
     * @param {string} options.userId - User identifier
     * @param {string} options.appSecret - Application secret key
     * @param {string} options.licenseKey - License key
     * @returns {Object} Request configuration
     * @throws {Error} If required parameters are missing
     */
    settings: ({ userId, appSecret, licenseKey }) => {
      validateSensaiParams({ userId })

      return {
        endpoint: SENSAI_ENDPOINTS.COACHES,
        method: 'GET',
        baseURL: BASE_URL,
        params: {
          ...SENSAI_CONFIG.DEFAULT_PARAMS,
          userId,
        },
        headers: {
          ...SENSAI_CONFIG.HEADERS.DEFAULT,
          appsecret: appSecret,
          licensekey: licenseKey,
          organizationId: userId,
        },
      }
    },
  },

  createCoach: {
    validateRequest: true,
    ignoreNotFound: true,
    /**
     * @description Create a new AI coach
     * @param {Object} options
     * @param {string} options.userId - User identifier
     * @param {string} options.userName - User's name
     * @param {Array} options.userknowledgeBase - Knowledge base content
     * @param {string} options.appSecret - Application secret key
     * @param {string} options.licenseKey - License key
     * @returns {Object} Request configuration
     * @throws {Error} If required parameters are missing
     */
    settings: ({ userId, appSecret, licenseKey }) => {
      validateSensaiParams({ userId })

      return {
        endpoint: SENSAI_ENDPOINTS.CREATE_COACH(userId),
        method: 'GET',
        baseURL: BASE_URL,
        headers: {
          ...SENSAI_CONFIG.HEADERS.DEFAULT,
          appsecret: appSecret,
          licensekey: licenseKey,
          organizationId: userId,
        },
      }
    },
  },

  getCoachStatus: {
    validateRequest: true,
    ignoreNotFound: true,
    useExternalRequest: true,
    settings: ({ requestId, appSecret, licenseKey, userId }) => {
      if (!requestId) {
        throw new Error('requestId is required')
      }

      return {
        endpoint: SENSAI_ENDPOINTS.COACH_STATUS(requestId),
        method: 'GET',
        baseURL: BASE_URL,
        params: {
          requestId,
        },
        headers: {
          ...SENSAI_CONFIG.HEADERS.DEFAULT,
          licenseKey,
          appSecret,
          organizationId: userId,
        },
      }
    },
  },

  meetSensai: {
    /**
     * @description Get request settings (gamification endpoint - meet sensai)
     * @param {object} options
     * @param {number} options.accountId
     * @returns {object} request settings
     */
    settings: ({ accountId }) => ({
      endpoint: `/account/${accountId}/gamification/action/meet-sensai`,
      body: {},
      method: 'POST',
    }),
  },

  fireProfileViewTrigger: {
    /**
     * @description Fire a SenSai profile-view trigger after the user has dwelled
     * on a colleague's profile page. The backend decides whether a coaching
     * insight should be surfaced.
     * @param {object} options
     * @param {number} options.accountId
     * @param {number} options.colleagueProfileId - Profile id of the colleague being viewed
     * @returns {object} request settings
     */
    settings: ({ accountId, colleagueProfileId }) => {
      validateSensaiParams({ accountId })

      return {
        endpoint: SENSAI_ENDPOINTS.PROFILE_VIEW_INSIGHT(accountId),
        body: {
          colleagueProfileId: parseInt(colleagueProfileId, 10),
        },
        method: 'POST',
      }
    },
  },

  trackInsightDismissed: {
    /**
     * @description Gamification event - user dismissed the SenSai profile-view
     * insight panel. The path segment is the event name itself.
     * @param {object} options
     * @param {number} options.accountId
     * @returns {object} request settings
     */
    settings: ({ accountId }) => ({
      endpoint: `/account/${accountId}/gamification/action/sensai_insight_dismissed`,
      body: {},
      method: 'POST',
    }),
  },

  trackInsightCtaClicked: {
    /**
     * @description Gamification event - user clicked the footnote CTA inside
     * the SenSai profile-view insight panel. The path segment is the event
     * name itself.
     * @param {object} options
     * @param {number} options.accountId
     * @returns {object} request settings
     */
    settings: ({ accountId }) => ({
      endpoint: `/account/${accountId}/gamification/action/sensai_insight_cta_clicked`,
      body: {},
      method: 'POST',
    }),
  },
}
